import { FC, memo, useRef, useState } from "react";
import { InputSelect } from "@/components/atoms";
import NewVirtualOrders from "./NewVirtualOrders";
import { PhotoWaterMarkProps } from "./OrderService.interface";

const PositionIcon = ({ pos }: { pos: 'TL' | 'TR' | 'BL' | 'BR' | 'MM' }) => {
    const rects = {
        BL: <rect x="4" y="14" width="6" height="6" fill="currentColor"/>,
        BR: <rect x="14" y="14" width="6" height="6" fill="currentColor"/>,
        MM: <rect x="9" y="9" width="6" height="6" fill="currentColor"/>,
        TL: <rect x="4" y="4" width="6" height="6" fill="currentColor"/>,
        TR: <rect x="14" y="4" width="6" height="6" fill="currentColor"/>,
    }

    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
            <rect x="2" y="2" width="20" height="20" rx="1" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.5"/>
            {rects[pos]}
        </svg>
    )
};


const Watermark : FC<PhotoWaterMarkProps> = ( {
  logoUrl,
  onlogoParamsChange,
  onNewImagemoptionVideo,
  onNewVirtualStaging,
  onResolutionsChange,
  showNewVirtualOrder,
  useLogo
}) =>  {

    // Watermark settings state
    const [watermarkPosition, setWatermarkPosition] = useState({ x: 85, y: 85 });
    const [watermarkOpacity, setWatermarkOpacity] = useState(70);
    const [watermarkSize, setWatermarkSize] = useState(15);
    const [isDragging, setIsDragging] = useState(false);
    const logoAspectRatio = 1;
    const [position, setPosition] = useState("");
    
    const [rightMove, setRightMove] = useState(false);
    const [medium, setMedium] = useState(false);
    const [highRes, setHighRes] = useState(false);

     const previewContainerRef = useRef<HTMLDivElement>(null);


     const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging || !previewContainerRef.current) return;
    
        const containerRect = previewContainerRef.current.getBoundingClientRect();
        
        let x = e.clientX - containerRect.left;
        let y = e.clientY - containerRect.top;
    
        const logoWidthPx = containerRect.width * (watermarkSize / 100);
        const logoHeightPx = logoWidthPx * logoAspectRatio;
    
        x = Math.max(0, Math.min(x, containerRect.width - logoWidthPx));
        y = Math.max(0, Math.min(y, containerRect.height - logoHeightPx));
    
        const xPercent = (x / containerRect.width) * 100;
        const yPercent = (y / containerRect.height) * 100;
        
        setWatermarkPosition({ x: xPercent, y: yPercent });
        onlogoParamsChange({ opacity: watermarkOpacity, position: `${xPercent},${yPercent}`, size: watermarkSize.toString() });
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        setIsDragging(true);
        handleMouseMove(e); 
        e.preventDefault();
    };

    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);

     const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newOpacity = parseInt(e.target.value, 10);
        e.preventDefault();
        setWatermarkOpacity(newOpacity);
        onlogoParamsChange({ opacity: newOpacity, position: `${watermarkPosition.x},${watermarkPosition.y}`, size: watermarkSize.toString() });
    }
    
    const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSize =  parseInt(e.target.value, 10);
        setWatermarkSize(newSize);
        onlogoParamsChange({ opacity: watermarkOpacity, position: `${watermarkPosition.x},${watermarkPosition.y}`, size: newSize.toString() });
    }

    const handleResolutionsChange = (checked: boolean, size: string) => {
        onResolutionsChange(size, checked);
    };

    const setPredefinedPosition = (pos: 'TL' | 'TR' | 'BL' | 'BR' | 'MM') => {
        const margin = 2; // 2% margin from the edges
        const previewAspectRatio = 4 / 3;
        const watermarkHeightPercent = watermarkSize * logoAspectRatio * previewAspectRatio;

        let newPosition = { x: 0, y: 0 };

        switch (pos) {
            case 'TL':
                newPosition = { x: margin, y: margin };
                break;
            case 'TR':
                newPosition = { x: 100 - watermarkSize - margin, y: margin };
                break;
            case 'BL':
                newPosition = { x: margin, y: 100 - watermarkHeightPercent - margin };
                break;
            case 'BR':
                newPosition = { x: 100 - watermarkSize - margin, y: 100 - watermarkHeightPercent - margin };
                break;
            case 'MM':
                newPosition = { x: (100 - watermarkSize) / 2, y: (100 - watermarkHeightPercent) / 2 };
                break;
        }

        // Clamp values to stay within bounds
        const x = Math.max(0, Math.min(newPosition.x, 100 - watermarkSize));
        const y = Math.max(0, Math.min(newPosition.y, 100 - watermarkHeightPercent));

        setWatermarkPosition({ x, y });
        onlogoParamsChange({ opacity: watermarkOpacity, position: `${x},${y}`, size: watermarkSize.toString() });
    };


    return (
        <div className="module-footer photos-selector-footer upsell-container">
            <span className="checkboxes size-checkboxes">
                <InputSelect
                    checked={rightMove}
                    type="checkbox"
                    label="Rightmove &amp; Zoopla"
                    onChangeValue={(checked: boolean) => {
                        setRightMove(checked);
                        handleResolutionsChange(checked, 'smallSize');
                    }}
                    value={rightMove.toString()}
                />
                <InputSelect
                    checked={medium}
                    type="checkbox"
                    label="Medium"
                    onChangeValue={(checked: boolean) => {
                        setMedium(checked);
                        handleResolutionsChange(checked, 'mediumSize');
                    }}
                    value={medium.toString()}
                />
                <InputSelect
                    checked={highRes}
                    type="checkbox"
                    label="High-Res Full Size (For print)"
                    onChangeValue={(checked: boolean) => {
                        setHighRes(checked);
                        handleResolutionsChange(checked, 'fullSize');
                    }}
                    value={highRes.toString()}
                />
            </span>
            {useLogo && <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4" style={{maxWidth: "800px" }}>
                {/* Preview Pane */}
                <div 
                    ref={previewContainerRef}
                    className={`relative w-full aspect-[4/3] bg-gray-900 rounded-lg overflow-hidden select-none cursor-move`}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                >
                    <img src="https://photoplanet1.s3.eu-west-1.amazonaws.com/PhotoplanetExamples/Slideshow+Thumbnail+-+1.jpg" alt="Preview" className="w-full h-full object-cover" />
                    <div
                            className="absolute pointer-events-none"
                            style={{
                                left: `${watermarkPosition.x}%`,
                                opacity: watermarkOpacity / 100,
                                top: `${watermarkPosition.y}%`,
                                width: `${watermarkSize}%`,
                            }}
                            >
                            <img src={logoUrl} alt="Watermark Preview" className="w-full h-auto" />
                    </div>
                </div>

                {/* Controls Pane */}
                    <div>
                        <p className="text-sm font-medium text-gray-800 mb-2">Position</p>
                        <p className="text-sm text-gray-700">Click a button for a preset position, or drag inside the preview.</p>
                        <div className="flex items-center space-x-2 mt-3">
                            {(['TL', 'TR', 'BL', 'BR', 'MM'] as const).map((pos) => {
                                    const titles = { BL: 'Bottom Left', BR: 'Bottom Right', MM: 'Center', TL: 'Top Left', TR: 'Top Right' };
                                    
                                    return (
                                    <button 
                                        key={pos}
                                        onClick={() => setPredefinedPosition(pos)}
                                        title={titles[pos]}
                                        className="p-2 rounded-md text-gray-600 hover:bg-gray-800 hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-colors"
                                    >
                                        <PositionIcon pos={pos} />
                                    </button>
                                    );
                            })}
                        </div>
                        <div>
                        <label htmlFor="opacity" className="block text-sm font-medium text-gray-800 mt-6">Opacity: {watermarkOpacity}%</label>
                        <input
                            type="range"
                            id="opacity"
                            min="0"
                            max="100"
                            step="5"
                            value={watermarkOpacity}
                            onChange={(e) => handleOpacityChange(e)}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-600 mt-2"
                        />
                    </div>
                    <div>
                        <label htmlFor="size" className="block text-sm font-medium text-gray-800 mt-6">Size: {watermarkSize}% of width</label>
                        <input
                            type="range"
                            id="size"
                            min="5"
                            max="50"
                            step="1"
                            value={watermarkSize}
                            onChange={(e) => handleSizeChange(e)}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-600 mt-2"
                        />
                    </div>
                    </div>
                    
            </div> }
            { showNewVirtualOrder && <NewVirtualOrders onNewImagemoptionVideo={onNewImagemoptionVideo} onNewVirtualStaging={onNewVirtualStaging} /> }
        </div>
    )
}

export default memo(Watermark);